/**
 * Next Best Action — rule-based v1.
 *
 * Computes short, actionable suggestions for the lead workspace from the
 * portal stage engine plus CRM context. Pure and client-side; the schema
 * (id/source/tone) intentionally leaves room for agent-generated suggestions
 * to be merged in later (source: 'agent').
 */

import type { PortalStage } from '../types';
import type { PortalStageSignals } from './portalStageEngine';
import { portalStageIndex } from './portalStageEngine';

export type NextBestActionTone = 'action' | 'waiting' | 'info';

export interface NextBestAction {
  /** Stable id — the workspace maps these to one-click handlers. */
  id:
    | 'send-portal-link'
    | 'start-3d-model'
    | 'build-quote'
    | 'send-proposal'
    | 'follow-up-proposal'
    | 'mark-signed'
    | 'start-2d-plans'
    | 'stage-pinned'
    | 'assign-project-number';
  label: string;
  detail?: string;
  tone: NextBestActionTone;
  actionLabel?: string;
  source: 'rule' | 'agent';
  /** Stage the suggestion relates to (used to focus the step card). */
  stage?: PortalStage;
}

export interface NextBestActionContext {
  signals: PortalStageSignals;
  displayStage: PortalStage;
  proposalStatus?: string | null;
  /** Deal has design fee paid but no project number assigned yet. */
  needsProjectNumber?: boolean;
  hasPortal: boolean;
}

export function computeNextBestActions(ctx: NextBestActionContext): NextBestAction[] {
  const out: NextBestAction[] = [];
  const { signals, displayStage, proposalStatus } = ctx;
  const stored = signals.storedStage;

  if (ctx.needsProjectNumber) {
    out.push({
      id: 'assign-project-number',
      label: 'Design fee received — assign a project number',
      detail: 'Activates operational tracking: QuickBooks, Project Hub, and files.',
      tone: 'action',
      actionLabel: 'Assign number',
      source: 'rule',
    });
  }

  if (signals.manualOverride) {
    out.push({
      id: 'stage-pinned',
      label: 'Stage pinned after a manual retreat — auto-advance is paused',
      detail: 'Released models, sent proposals, and payments will not bump the timeline until you advance the stage again.',
      tone: 'info',
      source: 'rule',
      stage: stored,
    });
  }

  if (stored === 'design-fee' && !signals.designFeePaid && !signals.designFeeSkipped) {
    out.push({
      id: 'send-portal-link',
      label: ctx.hasPortal
        ? 'Waiting on design fee — share the portal link with the client'
        : 'No portal yet — create one to collect the design fee',
      tone: 'waiting',
      actionLabel: ctx.hasPortal ? 'Copy link' : undefined,
      source: 'rule',
      stage: 'design-fee',
    });
  }

  if (
    stored === 'design-fee' &&
    (signals.designFeePaid || signals.designFeeSkipped) &&
    !signals.manualOverride
  ) {
    out.push({
      id: 'start-3d-model',
      label: signals.designFeePaid
        ? 'Design fee paid — start the 3D model'
        : 'Design fee skipped — start the 3D model',
      tone: 'action',
      actionLabel: 'Advance to 3D Model',
      source: 'rule',
      stage: '3d-model',
    });
  }

  if (stored === '3d-model' && signals.hasModelContent) {
    out.push({
      id: 'build-quote',
      label: '3D model released — build and send the quote',
      tone: 'action',
      actionLabel: 'Go to Quote',
      source: 'rule',
      stage: 'quote',
    });
  }

  if (displayStage === 'quote' && (!proposalStatus || proposalStatus === 'draft')) {
    out.push({
      id: 'send-proposal',
      label: 'Proposal is still a draft — review and send it',
      tone: 'action',
      actionLabel: 'Open proposal',
      source: 'rule',
      stage: 'quote',
    });
  }

  if (displayStage === 'quote' && (proposalStatus === 'sent' || proposalStatus === 'opened')) {
    out.push({
      id: 'follow-up-proposal',
      label:
        proposalStatus === 'opened'
          ? 'Client opened the proposal — good time to follow up'
          : 'Proposal sent — awaiting client review',
      tone: 'waiting',
      actionLabel: 'View activity',
      source: 'rule',
      stage: 'quote',
    });
  }

  const signedStageIdx = portalStageIndex('quote-signed');
  const atOrPastSignedStage =
    portalStageIndex(stored) >= signedStageIdx ||
    portalStageIndex(displayStage) >= signedStageIdx;

  if (proposalStatus === 'approved' && !atOrPastSignedStage && !signals.manualOverride) {
    out.push({
      id: 'mark-signed',
      label: 'Proposal approved — mark the quote signed',
      tone: 'action',
      actionLabel: 'Advance to Signed',
      source: 'rule',
      stage: 'quote-signed',
    });
  }

  if (stored === 'quote-signed') {
    out.push({
      id: 'start-2d-plans',
      label: 'Quote signed — kick off engineered 2D plans',
      tone: 'action',
      actionLabel: 'Advance to Plans',
      source: 'rule',
      stage: '2d-plans',
    });
  }

  // Keep the strip focused: max 3 suggestions, actions first.
  const toneRank: Record<NextBestActionTone, number> = { action: 0, waiting: 1, info: 2 };
  return out.sort((a, b) => toneRank[a.tone] - toneRank[b.tone]).slice(0, 3);
}
